// ---------------------------------------------------------------------------
// V27 — THE CASCADE, AND THE PEOPLE STANDING BEHIND IT.
//
// `console.events.offers`, at `events/:id/offers`, beside the ranking it is built
// on and the board it feeds.
//
// ---------------------------------------------------------------------------
// EVERY TIME ON THIS SCREEN CAME FROM THE SERVER
//
// `closes_at` is derived in SQL from the tier's PLANNED opening. This file never
// adds `response_minutes` to `opens_at`, and that is not a style rule: the
// browser's clock belongs to the person reading the screen, and a deadline drawn
// from it would be a deadline they could move. The one thing this file does
// compute from a local clock is the *reading* of a tier's row — and it does not,
// because the state and its tone arrive from the server too.
//
// ---------------------------------------------------------------------------
// `due` IS THE ROW THAT MATTERS AND IT IS NOT A STAGE OF THE CASCADE
//
// A tier whose opening has passed with nothing sent means the scheduler has not
// run. It is amber, it says "Due — not sent", and the sentence beside it names
// what to do. Collapse it into "waiting" and an outage is invisible on the one
// screen that would have shown it.
//
// ---------------------------------------------------------------------------
// AN OFFER IS NOT AN AGREEMENT, AND THE COLUMNS SAY SO SEPARATELY
//
// `offered_count`, `answered_count` and `accepted_count` are three numbers
// because they are three facts. A single "filled" figure would count every
// outstanding offer as a person who had said yes, which is exactly the reading
// that makes a cascade stop escalating while a shift goes unstaffed.
//
// NO NEW CSS. `stack`, `panel`, `data-table`, `field`, `btn`, `muted`, `numeric`
// all already carry rules in `web/src/design`.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ as consoleApi } from "../api/console";
import { match, type MatchRun } from "../api/match";
import { offers, type OfferReserve, type OfferWave, type StandbyRate } from "../api/offers";
import { Tabs } from "../design/Tabs";
import { AddButton } from "../design/AddLink";
import { Notice } from "../design/Notice";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { TONES, type Tone } from "../design/tone";
import { money } from "../system/format";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { whenIn } from "../system/format";

type Post = { id: string; zone: string | null; role_name?: string; starts_at: string; ends_at: string };
type RateBasis = { id: string; label: string };

/**
 * The server's tone, narrowed to the four this design system has.
 *
 * A TONE THAT IS NOT ONE OF THE FOUR FALLS BACK TO `neutral` RATHER THAN
 * THROWING. `statusChip` throws on an unknown tone, and a fifth tone added to
 * `app.offer_wave_state` next year must not take the whole screen down — the row
 * still carries its LABEL, which is what a reader needs. Status is never carried
 * by colour alone here for exactly this reason.
 */
function toneOf(value: string): Tone {
  return (TONES as readonly string[]).includes(value) ? (value as Tone) : "neutral";
}

export function Offers() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const params = useParams();
  const eventId = params["id"] ?? "";

  const [posts, setPosts] = useState<Post[] | null>(null);
  const [postId, setPostId] = useState("");
  const [waves, setWaves] = useState<OfferWave[] | null>(null);
  const [reserves, setReserves] = useState<OfferReserve[]>([]);
  const [standby, setStandby] = useState<StandbyRate | null>(null);
  const [runs, setRuns] = useState<MatchRun[] | null>(null);
  const [bases, setBases] = useState<RateBasis[] | null>(null);
  // NOT DERIVED FROM `runs.length === 0`. A list that came back empty and a list
  // that refused are the same value with different causes — failure form 11 — and
  // the sentence a scheduler needs is opposite in each case: "rank the bench" is
  // advice, and it is wrong advice when the ranking exists and could not be read.
  const [runsFailed, setRunsFailed] = useState(false);
  // AND THE SAME DISTINCTION FOR THE RESERVES. They arrive on the cascade's own
  // response, so a refused read left them as an empty array and the screen then
  // said "nobody is held in reserve" and "this post offers no standby payment" —
  // two confident statements of fact about a question it had been told it may not
  // ask. The gallery's `failed` cell is where that is visible and no test can see it.
  const [readFailed, setReadFailed] = useState(false);

  const [mayPlan, setMayPlan] = useState(false);
  const [mayFire, setMayFire] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // The cascade's read, apart from an action's refusal: a limit is calm, a fault can be tried again.
  const [loadFailed, setLoadFailed] = useState<Failure | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [planning, setPlanning] = useState(false);

  const [runId, setRunId] = useState("");
  const [tierSize, setTierSize] = useState(3);
  const [responseMinutes, setResponseMinutes] = useState(60);
  const [rateMinor, setRateMinor] = useState("");
  const [basis, setBasis] = useState("");

  useEffect(() => {
    void (async () => {
      const res = await send<{ requirements?: Post[] }>(consoleApi.listRequirements(apiBase, eventId));
      const rows = res.status === 200 && Array.isArray(res.body?.requirements) ? res.body.requirements : [];
      setPosts(rows);
      if (rows.length > 0 && postId === "") setPostId(rows[0]!.id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, eventId]);

  useEffect(() => {
    void (async () => {
      const res = await send<{ rate_bases?: RateBasis[] }>(consoleApi.listRateBases(apiBase));
      setBases(res.status === 200 && Array.isArray(res.body?.rate_bases) ? res.body.rate_bases : []);
    })();
  }, [apiBase]);

  const load = useCallback(async () => {
    if (postId === "") return;
    const res = await send<{
      waves?: OfferWave[];
      reserves?: OfferReserve[];
      standby?: StandbyRate | null;
      may_plan?: boolean;
      may_fire?: boolean;
      error?: { message: string };
    }>(offers.waves(apiBase, postId));
    if (res.status === 200 && Array.isArray(res.body?.waves)) {
      setWaves(res.body.waves);
      setReserves(res.body.reserves ?? []);
      setStandby(res.body.standby ?? null);
      setMayPlan(res.body.may_plan === true);
      setMayFire(res.body.may_fire === true);
      setRateMinor(
        res.body.standby && res.body.standby.rate_minor !== null
          ? String(res.body.standby.rate_minor)
          : "",
      );
      setBasis(res.body.standby?.basis ?? "");
      setReadFailed(false);
      setFailed(null);
      setLoadFailed(null);
    } else {
      setWaves(null);
      setReadFailed(true);
      setLoadFailed({ status: res.status, refusal: refusal(res.body?.error) });
    }
  }, [apiBase, postId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (postId === "") return;
    void (async () => {
      const res = await send<{ runs?: MatchRun[] }>(match.runs(apiBase, postId));
      const good = res.status === 200 && Array.isArray(res.body?.runs);
      const rows = good ? (res.body!.runs as MatchRun[]) : [];
      setRunsFailed(!good);
      setRuns(rows);
      setRunId(rows.length > 0 ? rows[0]!.run_id : "");
    })();
  }, [apiBase, postId]);

  const plan = useCallback(async () => {
    setBusy(true);
    const res = await send<{ error?: { message: string } }>(
      offers.plan(apiBase, postId, {
        match_run_id: runId,
        tier_size: tierSize,
        response_minutes: responseMinutes,
      }),
    );
    setBusy(false);
    if (res.status >= 400) {
      setFailed(refusal(res.body?.error) ?? t("That cascade could not be laid out."));
      return;
    }
    setSaid(
      t("Planned. Every tier's deadline is fixed from the moment it was laid out, so a late run of the cascade does not move anybody's deadline."),
    );
    await load();
  }, [apiBase, postId, runId, tierSize, responseMinutes, load]);

  const fire = useCallback(async () => {
    setBusy(true);
    const res = await send<{
      cascade?: { offers_expired: number; waves_skipped: number; waves_fired: number; offers_made: number };
      error?: { message: string };
    }>(offers.fire(apiBase));
    setBusy(false);
    if (res.status >= 400 || !res.body?.cascade) {
      setFailed(refusal(res.body?.error) ?? t("The cascade could not be run."));
      return;
    }
    const c = res.body.cascade;
    setSaid(
      t("{fired} tier(s) contacted, {made} offer(s) made, {expired} lapsed offer(s) released, {skipped} tier(s) no longer needed.", {
        fired: c.waves_fired,
        made: c.offers_made,
        expired: c.offers_expired,
        skipped: c.waves_skipped,
      }),
    );
    await load();
  }, [apiBase, load]);

  const saveStandby = useCallback(async () => {
    setBusy(true);
    const payload =
      rateMinor.trim() === "" || basis === ""
        ? {}
        : { rate_minor: Number(rateMinor), basis };
    const res = await send<{ error?: { message: string } }>(
      offers.setStandby(apiBase, postId, payload),
    );
    setBusy(false);
    if (res.status >= 400) {
      setFailed(refusal(res.body?.error) ?? t("That standby rate could not be saved."));
      return;
    }
    setSaid(
      rateMinor.trim() === ""
        ? t("Saved. This post offers no standby payment.")
        : t("Saved. Named reserves are paid this for holding themselves available."),
    );
    await load();
  }, [apiBase, postId, rateMinor, basis, load]);

  /**
   * ONE TAP, AND IT GOES THROUGH V13's ROUTE.
   *
   * `app.apply_assignment_transition` writes the history row carrying
   * `from_is_reserve` and `to_is_reserve` and the audit row, in one transaction.
   * A second endpoint for the same act would be a second audit shape, and the
   * chain a tribunal reads would have two spellings of one event in it.
   *
   * The idempotency key is minted HERE and held for the lifetime of the tap. Mint
   * it inside the request and a retry carries a fresh key and promotes twice.
   */
  const promote = useCallback(
    async (assignmentId: string) => {
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(
        consoleApi.transitionAssignment(
          apiBase,
          assignmentId,
          { is_reserve: false },
          `promote-${assignmentId}-${Date.now()}`,
        ),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That person could not be promoted."));
        return;
      }
      setSaid(t("Promoted onto the working crew. The change is in this assignment's history."));
      await load();
    },
    [apiBase, load],
  );

  /**
   * NULL WHEN IT CANNOT BE NAMED, AND NEVER THE RAW ID.
   *
   * The fallback used to return the id, so a reference list that had not loaded
   * — or had been refused — printed a machine value into a sentence a scheduler
   * reads about money. It looked like a word somebody had chosen. A value this
   * screen cannot name is a thing it has to say it cannot name.
   */
  const basisLabel = (id: string | null): string | null => {
    if (id === null) return null;
    const found = (bases ?? []).find((b) => b.id === id);
    return found ? found.label : null;
  };

  return (
    <div className="stack">
      <h1>{t("Offers and reserves")}</h1>

        <div className="field">
          <label htmlFor="v27-post">{t("Post")}</label>
          <select id="v27-post" value={postId} onChange={(e) => setPostId(e.target.value)}>
            {(posts ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.zone ?? (p.role_name ? translateLabel(locale, p.role_name) : t("This post"))} · {whenIn(p.starts_at, orgZone)}
              </option>
            ))}
          </select>
        </div>
        {posts !== null && posts.length === 0 ? (
          <p className="muted">{t("This event has no posts yet, so there is nothing to cascade.")}</p>
        ) : null}

      <Tabs
        label={t("Offers and reserves")}
        tabs={[
          {
            id: "offers-cascade",
            label: t("The cascade"),
            content: (
            <section className="panel stack">
              <div className="panel-head">
                <h2>{t("The cascade")}</h2>
                {mayPlan ? <AddButton label={t("Lay out a cascade")} expanded={planning} onClick={() => setPlanning(!planning)} /> : null}
              </div>
              {loadFailed !== null ? <LoadFailed failure={loadFailed} fallback={t("This cascade could not be read.")} onRetry={() => void load()} /> : null}
              {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
              {said !== null ? <StatusChip tone="live" label={said} /> : null}


              {waves !== null && waves.length === 0 ? (
                <p>
                  <StatusChip tone="neutral" label={t("No cascade yet")} />{" "}
                  <span className="muted">{t("Nobody has been contacted about this post through a cascade. Lay one out below, or offer the work directly from the crew screen.")}</span>
                </p>
              ) : null}

              {waves !== null && waves.length > 0 ? (
                <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("Tier")}</th>
                      <th scope="col">{t("Ranked")}</th>
                      <th scope="col">{t("Opens")}</th>
                      <th scope="col">{t("Closes")}</th>
                      <th scope="col">{t("State")}</th>
                      <th scope="col">{t("Offered")}</th>
                      <th scope="col">{t("Answered")}</th>
                      <th scope="col">{t("Accepted")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waves.map((w) => (
                      <tr key={w.wave_id}>
                        <td className="numeric">{w.tier}</td>
                        <td>
                          {w.from_rank}&ndash;{w.to_rank}
                        </td>
                        <td>{whenIn(w.opens_at, orgZone)}</td>
                        <td>{whenIn(w.closes_at, orgZone)}</td>
                        <td>
                          {/* THE LABEL AND THE TONE BOTH COME FROM THE SERVER, and the
                              chip carries an icon as well as a colour. This is read on a
                              cracked phone, in the rain, at 06:00. */}
                          <StatusChip tone={toneOf(w.tone)} label={translateLabel(locale, w.state_label)} />
                        </td>
                        <td className="numeric">{w.offered_count}</td>
                        <td className="numeric">{w.answered_count}</td>
                        <td className="numeric">{w.accepted_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {waves.some((w) => w.waits_on_a_run) ? (
                  <p className="muted">
                    {t("A tier is due and nothing has been sent. That is the cascade waiting on somebody to run it, not a stage it passes through. Use “Run the cascade now” above.")}
                  </p>
                ) : null}
                  {mayFire ? (
                    <div className="row">
                      <button type="button" className="btn" disabled={busy} onClick={() => void fire()}>
                        {t("Run the cascade now")}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}

              {mayPlan && planning ? (
                <div className="stack">
                  <div className="form-grid">
                <div className="field">
                  <label htmlFor="v27-run">{t("Ranking")}</label>
                  <select id="v27-run" value={runId} onChange={(e) => setRunId(e.target.value)}>
                    {(runs ?? []).map((r) => (
                      <option key={r.run_id} value={r.run_id}>
                        {whenIn(r.ranked_at, orgZone)} · {t("{n} ranked", { n: r.ranked_count })}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="v27-tier-size">{t("People per tier")}</label>
                  <input
                    id="v27-tier-size"
                    type="number"
                    min={1}
                    max={100}
                    value={tierSize}
                    onChange={(e) => setTierSize(Number(e.target.value))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="v27-window">{t("Minutes to answer")}</label>
                  <input
                    id="v27-window"
                    type="number"
                    min={5}
                    max={10080}
                    value={responseMinutes}
                    onChange={(e) => setResponseMinutes(Number(e.target.value))}
                  />
                </div>
                  </div>
                  {runsFailed ? (
                  <Notice tone="failed">{t("The rankings for this post could not be read, so no cascade can be laid out from here. This is not the same as the post never having been ranked.")}</Notice>
                ) : null}
                {!runsFailed && runs !== null && runs.length === 0 ? (
                  <p className="muted">
                    {t("This post has not been ranked yet. Rank the bench against it first — a cascade with no order to it is a mass email.")}
                  </p>
                ) : null}
                  <div className="row">
                    <button
                      type="button"
                      className="btn"
                      disabled={busy || runId === "" || postId === ""}
                      onClick={() => void plan().then(() => setPlanning(false))}
                    >
                      {t("Lay out the cascade")}
                    </button>
                    <button type="button" className="btn btn-quiet" onClick={() => setPlanning(false)}>
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              ) : null}

              <details className="role-group">
                <summary>{t("How a cascade works")}</summary>
                <p className="muted">
                  {t("A cascade offers one post to the ranking a tier at a time. Each tier gets a fixed window to answer; whoever does not answer is released and the next tier follows. Every deadline is fixed when the cascade is laid out, so running it late does not move anybody’s deadline.")}
                </p>
                <p className="muted">
                  {t("The tiers are bands in a ranking, so who is contacted first is answerable from a record that already carries a sentence per person. A cascade is planned once: it is the record of what was decided, and replanning would rewrite deadlines people have already been given.")}
                </p>
              </details>
            </section>
            ),
          },
          {
            id: "offers-reserves",
            label: t("Named reserves"),
            content: (
            <section className="panel">
              <h2>{t("Named reserves")}</h2>
              <p className="muted">
                {t("People held in reserve for this post. A reserve does not take a place on the crew until somebody promotes them, and a reserve who has only been offered the standby has not yet agreed to anything.")}
              </p>
              {standby !== null && standby.rate_minor !== null ? (
                basisLabel(standby.basis) !== null ? (
                  <p className="muted">
                    {t("Standby pays {amount} {basis}.", {
                      amount: money(standby.rate_minor, standby.currency),
                      basis: translateLabel(locale, basisLabel(standby.basis)!).toLowerCase(),
                    })}
                  </p>
                ) : (
                  <p className="muted">
                    {t("Standby pays {amount}, but what that figure is paid against could not be read — so this page cannot tell you what a reserve is owed for one shift.", {
                      amount: money(standby.rate_minor, standby.currency),
                    })}
                  </p>
                )
              ) : readFailed ? (
                <p className="muted">{t("Whether this post pays a standby rate could not be read.")}</p>
              ) : (
                <p className="muted">{t("This post offers no standby payment.")}</p>
              )}

              {readFailed ? (
                <p className="muted">{t("Who is held in reserve for this post could not be read.")}</p>
              ) : reserves.length === 0 ? (
                <p className="muted">{t("Nobody is held in reserve for this post.")}</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("Name")}</th>
                      <th scope="col">{t("Where they stand")}</th>
                      <th scope="col">{t("Shift")}</th>
                      <th scope="col">{t("One tap")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reserves.map((r) => (
                      <tr key={r.assignment_id}>
                        <td>{r.worker_name}</td>
                        <td>
                          <StatusChip
                            tone={r.is_commitment ? "live" : "attention"}
                            label={
                              r.is_commitment
                                ? t("{status} — standing by", { status: translateLabel(locale, r.status_label) })
                                : t("{status} — has not answered", { status: translateLabel(locale, r.status_label) })
                            }
                          />
                        </td>
                        <td>{whenIn(r.starts_at, orgZone)}</td>
                        <td>
                          {mayPlan ? (
                            <button
                              type="button"
                              className="btn"
                              disabled={busy}
                              onClick={() => void promote(r.assignment_id)}
                            >
                              {t("Promote to the crew")}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
            ),
          },
          ...(mayPlan
            ? [
                {
                  id: "offers-standby",
                  label: t("What standby pays"),
                  content: (
                    <section className="panel">
                      <h2>{t("What standby pays")}</h2>
                      <p className="muted">
                        {t("What a named reserve is paid for holding themselves available, whether or not they are called. Leave the figure empty to say this post offers no standby payment — which is a different thing from zero.")}
                      </p>
                      <div className="field">
                        <label htmlFor="v27-standby">
                          {standby?.currency
                            ? t("Standby rate, in {currency} minor units", { currency: standby.currency })
                            : t("Standby rate, in the post's currency minor units")}
                        </label>
                        <input
                          id="v27-standby"
                          type="number"
                          min={0}
                          value={rateMinor}
                          onChange={(e) => setRateMinor(e.target.value)}
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="v27-basis">{t("Basis")}</label>
                        <select id="v27-basis" value={basis} onChange={(e) => setBasis(e.target.value)}>
                          <option value="">{t("No standby payment")}</option>
                          {(bases ?? []).map((b) => (
                            <option key={b.id} value={b.id}>
                              {translateLabel(locale, b.label)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button type="button" className="btn" disabled={busy || postId === ""} onClick={() => void saveStandby()}>
                        {t("Save what standby pays")}
                      </button>
                    </section>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}
